/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: Svelte 5 Runes State Management
 * PURPOSE: Consolidated authentication state management with JWT tokens, user sessions,
 * permissions (RBAC), organization data, and real-time permission sync
 *
 * NEW PATTERNS IMPLEMENTED:
 * - Runes Pattern: $state for reactive authentication state
 * - Derived State: $derived/$derived.by for computed auth checks and permissions
 * - Effect Pattern: $effect.root for cross-tab sync and session verification
 * - Accessor Pattern: Public getters/setters with internal state protection
 *
 * KEY FEATURES:
 * - JWT token management with auto-refresh and expiry tracking
 * - User profile with organization/department data (critical for file uploads)
 * - Permission system (RBAC + OPA) with real-time updates via WebSocket
 * - Cross-tab synchronization for token updates
 * - Session restoration from localStorage with validation
 * - Dashboard and component-level permission checks
 *
 * RELATED FILES:
 * - src/lib/services/websocket/permission-sync.ts - Real-time permission updates
 * - src/lib/services/api/auth.api.ts - Authentication API calls
 * - src/lib/types/core/user.types.ts - User and permission type definitions
 */

// src/lib/state/auth.state.svelte.ts
// Consolidated Auth State Management - State, actions, and derived values

import { browser } from '$app/environment';
import { goto } from '$app/navigation';
import { eventBus } from '$lib/core/services/EventBusService';
import type { LoginResponse } from '$lib/types/api/responses.types';
import type { UserPermissions, UserProfile } from '$lib/types/core/user.types';
import type { SessionState, TokenState } from '$lib/types/core/state.types';
import { createLogger } from '$lib/utils/logger';
import { UserLevel, ResourceType, ActionType } from '$lib/types/enums';
import { secureStorage } from '$lib/utils/secure-storage';

// PHASE 3.3: Authentication status state machine
export type AuthStatus =
	| 'initializing' // App starting up
	| 'unauthenticated' // No valid session
	| 'authenticating' // Login in progress
	| 'authenticated' // Valid session
	| 'refreshing' // Token refresh in progress
	| 'expired' // Token expired, need re-auth
	| 'error'; // Error state

const logger = createLogger('state.auth');
import { permissionSyncService } from '$lib/services/websocket/permission-sync';

// PHASE 3.2: BroadcastChannel for cross-tab communication
// NOTE: sessionStorage does NOT fire 'storage' events (that's localStorage only)
const authChannel = browser ? new BroadcastChannel('auth-sync') : null;

// --- Helper Functions (Private) ---

/**
 * Extract permissions from roles in the login response
 */
function extractPermissionsFromRoles(roles: LoginResponse['roles']): UserPermissions {
	logger.debug('Extracting permissions from roles', {
		roleCount: roles?.length,
		roleNames: roles?.map((r) => r.name)
	});

	const permissions: UserPermissions = {
		dashboards: {},
		features: {},
		direct_permissions: [],
		inherited_permissions: [],
		metadata: {}
	};

	roles.forEach((role) => {
		logger.debug('Processing role permissions', {
			roleName: role.name,
			hasPermissions: !!role.permissions,
			resourceCount: Object.keys(role.permissions || {}).length
		});

		if (role.permissions) {
			Object.entries(role.permissions).forEach(([resource, actions]) => {
				permissions.direct_permissions.push({
					resource,
					actions: Array.isArray(actions) ? actions : [actions],
					scope: role.code
				});
			});
		}
	});

	logger.debug('Permission extraction completed', {
		directPermissionsCount: permissions.direct_permissions.length,
		hasComponentPermissions: permissions.direct_permissions.some((p) => p.resource === 'COMPONENT')
	});

	return permissions;
}

function getInitialAuthState(): SessionState {
	// Default state, assuming server-side or browser start with no prior state
	let initialState: SessionState = {
		status: 'initializing', // PHASE 3.3: Start with initializing status
		authenticated: false,
		user: null,
		permissions: null,
		tokenExpiry: null,
		checking: false,
		error: null,
		ready: !browser // Ready immediately on server, browser will determine readiness after check
	};

	if (browser) {
		logger.debug('Initializing auth state in browser');
		// Don't automatically set checking to true - only if we need to verify
		try {
			// PHASE 3 SECURITY: Use sessionStorage via secureStorage wrapper
			const token = secureStorage.getToken();
			const userData = secureStorage.getUserData<UserProfile>();
			const permissions = secureStorage.getPermissions<UserPermissions>();
			const expiryDate = secureStorage.getTokenExpiry();

			if (token && userData) {
				const user = userData;
				let isExpired = true;

				if (expiryDate) {
					isExpired = expiryDate.getTime() < Date.now();
				}

				if (!isExpired) {
					logger.info('Session restored from sessionStorage', {
						userId: user.id,
						hasToken: !!token,
						orgCode: user.organization_code,
						roleCount: user.roles?.length || 0
					});
					initialState.user = user;
					initialState.permissions = permissions;
					initialState.authenticated = true;
					initialState.tokenExpiry = expiryDate;
					initialState.status = 'authenticated'; // PHASE 3.3: Set authenticated status
					// Don't leave checking as true when we have valid data
					initialState.checking = false;
				} else {
					logger.info('Session found but token expired');
					initialState.status = 'expired'; // PHASE 3.3: Token expired
					// Set checking to true so AuthService can try to refresh
					initialState.checking = true;
				}
			} else {
				logger.debug('No session found in sessionStorage');
				initialState.status = 'unauthenticated'; // PHASE 3.3: No session
				initialState.checking = false; // No session to check
				initialState.ready = true; // Ready immediately if no session
			}
		} catch (e) {
			logger.error('Failed to restore session', {
				error: e instanceof Error ? e.message : 'Unknown error'
			});
			initialState = {
				status: 'error', // PHASE 3.3: Error during initialization
				authenticated: false,
				user: null,
				permissions: null,
				tokenExpiry: null,
				checking: false,
				error: e instanceof Error ? e : new Error('Failed to restore auth state'),
				ready: true
			};
		} finally {
			// If we successfully restored a session, we're ready
			if (initialState.authenticated && initialState.user && !initialState.checking) {
				initialState.ready = true;
			} else {
				initialState.ready = !initialState.checking;
			}
			logger.debug('Initial auth state established', {
				ready: initialState.ready,
				authenticated: initialState.authenticated,
				checking: initialState.checking,
				hasUser: !!initialState.user
			});
		}
	}
	return initialState;
}

// --- Main Authentication State ---
const state = $state<SessionState>(getInitialAuthState());

// --- Token State ---
const tokenStateInternal = $state<TokenState>({
	isRefreshing: false,
	lastRefreshTime: null
});

// --- Internal Derived Values (prefixed with _ to avoid export conflicts) ---

/**
 * Derived authentication state check
 *
 * Returns true if:
 * - Running in browser context
 * - User has valid session (authenticated OR refreshing token)
 * - Token expiry is set and hasn't expired yet
 *
 * Note: 'refreshing' status is treated as authenticated because
 * token refresh is a proactive background operation that occurs
 * BEFORE the token expires. The user maintains their valid session
 * throughout the refresh process.
 */
// PHASE 3.3: Authentication status derived from state machine
const _isAuthenticated = $derived(
	browser &&
	(state.status === 'authenticated' || state.status === 'refreshing') &&
	state.tokenExpiry !== null &&
	state.tokenExpiry.getTime() > Date.now()
);

const _isReady = $derived(
	state.status !== 'initializing' && state.status !== 'authenticating'
);

// Backward compatibility - keep existing derived values
const _isAuthReady = $derived(browser ? state.ready : true);
const _isCheckingAuth = $derived(browser ? state.checking : false);

// User information
const _getUserName = $derived.by(() => (browser ? (state.user?.name ?? 'User') : 'User'));
const _getUserEmail = $derived.by(() => (browser ? (state.user?.email ?? '') : ''));
const _getUserId = $derived.by(() => state.user?.id ?? undefined);
const _getUserLevel = $derived.by(() => state.user?.user_level ?? undefined);
const _isAdmin = $derived.by(() => (browser ? state.user?.user_level === UserLevel.SYSTEM : false));
const _isCompanyUser = $derived.by(() => state.user?.is_company_user ?? false);

// Organization information - CRITICAL for file uploads (internal derived values)
const _getOrganizationCode = $derived.by(() => {
	const orgCode = state.user?.organization_code ?? undefined;
	if (!orgCode && state.user) {
		logger.warn('User missing organization_code', { userId: state.user.id });
	}
	return orgCode;
});
const _getOrganizationName = $derived.by(() => {
	const orgName = state.user?.organization_name ?? undefined;
	if (!orgName && state.user) {
		logger.warn('User missing organization_name', { userId: state.user.id });
	}
	return orgName;
});
const _getOrganizationId = $derived.by(() => {
	const orgId = state.user?.organization_id ?? undefined;
	if (!orgId && state.user) {
		logger.warn('User missing organization_id', { userId: state.user.id });
	}
	return orgId;
});

// Role and permission helpers (internal derived values)
const _hasRole = $derived.by(() => (roleCode: string) => {
	return state.user?.roles.some((r) => r.code === roleCode) ?? false;
});

const _hasDepartment = $derived.by(() => (deptCode: string) => {
	return state.user?.departments.some((d) => d.code === deptCode) ?? false;
});

const _isDepartmentManager = $derived.by(() => (deptCode?: string) => {
	if (!deptCode) {
		return state.user?.departments.some((d) => d.is_manager) ?? false;
	}
	return state.user?.departments.some((d) => d.code === deptCode && d.is_manager) ?? false;
});

const _hasPermission = $derived.by(() => {
	// Guard: If permissions are not yet loaded, return a safe default function that denies all
	// This ensures the derived value is ALWAYS a function, preventing "is not a function" errors
	if (!state.permissions || !state.permissions.direct_permissions) {
		return (resource: ResourceType | string, action: ActionType | string) => {
			return false;
		};
	}

	// Permissions are loaded - return the actual permission checking function
	// Capture permissions in closure for stability
	const currentPermissions = state.permissions;

	return (resource: ResourceType | string, action: ActionType | string) => {
		// TypeScript string enums ARE strings, so we can use them directly
		// Just coerce to string for consistency
		const resourceStr = String(resource);
		const actionStr = String(action);

		const hasIt = currentPermissions.direct_permissions.some(
			(p) => p.resource === resourceStr && p.actions.includes(actionStr)
		);

		return hasIt;
	};
});

// Dashboard and component permission helpers (consolidated from permissionStore.ts)
const _canAccessDashboard = $derived.by(() => (dashboardKey: string): boolean => {
	if (!state.permissions) {
		logger.debug('Dashboard access denied - no permissions', { dashboard: dashboardKey });
		return false;
	}

	const canAccess = !!state.permissions.dashboards?.[dashboardKey]?.visible;
	logger.debug('Dashboard access check', { dashboard: dashboardKey, granted: canAccess });
	return canAccess;
});

const _canAccessComponent = $derived(
	() =>
		(dashboardKey: string, componentKey: string): boolean => {
			if (!state.permissions) {
				logger.debug('Component access denied - no permissions', {
					dashboard: dashboardKey,
					component: componentKey
				});
				return false;
			}

			const canAccess =
				!!state.permissions.dashboards?.[dashboardKey]?.components?.[componentKey]?.visible;
			logger.debug('Component access check', {
				dashboard: dashboardKey,
				component: componentKey,
				granted: canAccess
			});
			return canAccess;
		}
);

const _canPerformAction = $derived(
	() =>
		(dashboardKey: string, componentKey: string, action: string): boolean => {
			if (!state.permissions) {
				logger.debug('Action denied - no permissions', {
					dashboard: dashboardKey,
					component: componentKey,
					action
				});
				return false;
			}

			const component = state.permissions.dashboards?.[dashboardKey]?.components?.[componentKey];
			const canPerform = !!component && !!(component as any)[action];
			logger.debug('Action permission check', {
				dashboard: dashboardKey,
				component: componentKey,
				action,
				granted: canPerform
			});
			return canPerform;
		}
);

const _hasFeature = $derived.by(() => (featureKey: string): boolean => {
	if (!state.permissions) {
		logger.debug('Feature access denied - no permissions', { feature: featureKey });
		return false;
	}

	const hasFeatureEnabled = !!state.permissions.features?.[featureKey];
	logger.debug('Feature permission check', { feature: featureKey, granted: hasFeatureEnabled });
	return hasFeatureEnabled;
});

// Token access helpers - PHASE 3 SECURITY: Use sessionStorage
const _getAccessToken = $derived.by(() => {
	if (!browser) return null;
	return secureStorage.getToken();
});

const _getTokenType = $derived.by(() => {
	if (!browser) return 'Bearer';
	return secureStorage.getTokenType();
});

const _getAuthHeader = $derived.by(() => {
	const token = _getAccessToken;
	if (!token) return null;
	return `${_getTokenType} ${token}`;
});

// --- Action Methods (Private Functions) ---

/**
 * Set authenticated state from login response
 * Stores ALL fields from the login response including organization data
 */
function setAuthenticatedFromLoginInternal(loginResponse: LoginResponse): void {
	logger.info('Setting authenticated state from login', {
		userId: loginResponse.user_id,
		orgCode: loginResponse.organization_code,
		roleCount: loginResponse.roles?.length || 0
	});

	// Log role information without sensitive data
	if (loginResponse.roles) {
		logger.debug('User roles loaded', {
			roleNames: loginResponse.roles.map((r) => r.name),
			totalResources: loginResponse.roles.reduce(
				(sum, r) => sum + Object.keys(r.permissions || {}).length,
				0
			)
		});
	}

	// Build complete user profile with ALL fields from login response
	const userProfile: UserProfile = {
		// Core user fields from login response
		id: loginResponse.user_id,
		email: loginResponse.email,
		name: loginResponse.name,
		organization_id: loginResponse.organization_id,

		// Organization data - these are crucial for file uploads and API calls
		organization_name: loginResponse.organization_name,
		organization_code: loginResponse.organization_code,

		// User status and tracking
		is_company_user: loginResponse.is_company_user,
		user_level: loginResponse.user_level,
		last_login: loginResponse.last_login,

		// Role and department data
		roles: loginResponse.roles,
		departments: loginResponse.departments,

		// Fields that may not be in login response but are required by User interface
		is_active: true, // Assume active if they can login
		primary_department_id: loginResponse.departments?.[0]?.id || null,
		login_count: 0, // Will be updated from /me endpoint
		failed_login_attempts: 0,
		last_failed_login: null,
		last_active: new Date().toISOString(),
		is_locked: false,
		locked_at: null,
		locked_reason: null,
		require_password_change: false,
		is_system_user: false,
		mfa_enabled: false,
		permission_sync_version: 0,
		last_permission_sync: null,
		created_at: new Date().toISOString(),
		updated_at: new Date().toISOString(),

		// User preferences (will be loaded separately)
		preferences: {},
		notification_settings: {},
		ui_settings: {}
	};

	// Extract permissions from roles
	const permissions = extractPermissionsFromRoles(loginResponse.roles);

	// Calculate token expiry
	let tokenExpiry: Date | null = null;
	if (loginResponse.access_token_expires_at) {
		try {
			tokenExpiry = new Date(loginResponse.access_token_expires_at);
			logger.debug('Token expiry calculated', { expiresAt: tokenExpiry.toISOString() });
		} catch (e) {
			logger.error('Failed to parse token expiry', {
				error: e instanceof Error ? e.message : 'Unknown error'
			});
		}
	}

	// Update state
	setAuthenticatedInternal(userProfile, permissions, tokenExpiry);

	// Initialize permission sync service for real-time updates
	if (browser && userProfile.id) {
		permissionSyncService.initialize(userProfile.id).catch((error) => {
			logger.error('Failed to initialize permission sync', {
				error: error instanceof Error ? error.message : 'Unknown error',
				userId: userProfile.id
			});
		});
	}

	logger.info('Authentication state updated successfully');
}

/**
 * Set authenticated state (can be called from other sources like session restore)
 */
function setAuthenticatedInternal(
	user: UserProfile,
	permissions: UserPermissions | null = null,
	tokenExpiry?: Date | null
) {
	logger.debug('Setting authenticated state', {
		userId: user?.id,
		orgCode: user?.organization_code,
		hasOrgData: !!(user?.organization_code && user?.organization_name),
		permissionCount: permissions?.direct_permissions?.length || 0
	});

	// Validate that we have organization data
	if (!user?.organization_code) {
		logger.warn('User profile missing organization_code', { userId: user?.id });
	}

	logger.debug('Storing permissions in auth state', {
		directPermissionsCount: permissions?.direct_permissions?.length || 0,
		hasComponentPermissions: permissions?.direct_permissions?.some(
			(p) => p.resource === 'COMPONENT'
		)
	});

	// PHASE 3.3: Update state machine status
	state.status = 'authenticated';
	state.authenticated = true;
	state.user = user;
	state.permissions = permissions;
	state.checking = false;
	state.error = null;
	state.ready = true;

	if (tokenExpiry !== undefined) {
		state.tokenExpiry = tokenExpiry;
	}

	if (browser) {
		try {
			// PHASE 3 SECURITY: Store auth data in sessionStorage
			// Note: Token storage is handled by auth.api.ts
			// We only update the user data and permissions here
			secureStorage.setUserData(user);
			if (permissions) {
				secureStorage.setPermissions(permissions);
			} else {
				secureStorage.clearPermissions();
			}

			if (state.tokenExpiry) {
				secureStorage.setTokenExpiry(state.tokenExpiry);
				secureStorage.updateTokenTimestamp();
			}

			// PHASE 3.2: Broadcast login to other tabs
			// Only send cloneable data through BroadcastChannel
			if (authChannel) {
				authChannel.postMessage({
					type: 'login'
					// Don't send the full user object as it may contain non-cloneable properties
					// Other tabs will restore from secureStorage
				});
				logger.debug('[Auth] Login broadcast to other tabs');
			}

			eventBus.emit('auth:state-updated', { user });
			logger.debug('Authentication state persisted to sessionStorage');
		} catch (e) {
			logger.error('Failed to persist authentication state', {
				error: e instanceof Error ? e.message : 'Unknown error'
			});
		}
	}
}

/**
 * Update user profile (e.g., after fetching from /me endpoint)
 */
function updateUserProfileInternal(updates: Partial<UserProfile>): void {
	if (!state.user) return;

	logger.debug('Updating user profile', {
		userId: state.user.id,
		updateFields: Object.keys(updates),
		preservedOrgCode: !updates.organization_code && !!state.user.organization_code
	});

	// Preserve organization data if not included in updates
	const updatedUser = {
		...state.user,
		...updates,
		// Ensure we never lose organization data
		organization_code: updates.organization_code || state.user.organization_code,
		organization_name: updates.organization_name || state.user.organization_name
	};

	state.user = updatedUser;

	if (browser) {
		try {
			// PHASE 3 SECURITY: Store updated profile in sessionStorage
			secureStorage.setUserData(state.user);
			eventBus.emit('auth:profile-updated', { user: state.user });
			logger.debug('User profile updated successfully');
		} catch (e) {
			logger.error('Failed to update user profile', {
				error: e instanceof Error ? e.message : 'Unknown error'
			});
		}
	}
}

/**
 * Clear authentication state
 */
function clearAuthenticationInternal(options?: { skipBroadcast?: boolean }) {
	logger.info('Clearing authentication state');

	// PHASE 3.3: Update state machine status
	state.status = 'unauthenticated';
	state.authenticated = false;
	state.user = null;
	state.permissions = null;
	state.tokenExpiry = null;
	state.checking = false;
	state.error = null;
	state.ready = true;

	if (browser) {
		try {
			// PHASE 3 SECURITY: Clear all sessionStorage auth data
			secureStorage.clearAll();

			// Cleanup permission sync service
			permissionSyncService.cleanup();

			// PHASE 3.2: Broadcast logout to other tabs
			// Skip broadcast when called from cross-tab handler to prevent infinite loop
			if (authChannel && !options?.skipBroadcast) {
				authChannel.postMessage({ type: 'logout' });
				logger.debug('[Auth] Logout broadcast to other tabs');
			}

			eventBus.emit('auth:logout', {});
			logger.info('Authentication cleared successfully');
		} catch (e) {
			logger.error('Failed to clear authentication state', {
				error: e instanceof Error ? e.message : 'Unknown error'
			});
		}
	}
}

/**
 * Set fresh permissions fetched from backend
 * This is used to update permissions after OPA sync or role changes
 */
function setFreshPermissionsInternal(permissionsData: {
	direct_permissions: Array<{ resource: string; actions: string[]; scope?: string }>;
}): void {
	if (!state.user) {
		logger.warn('Cannot set permissions - no authenticated user');
		return;
	}

	logger.info('Refreshing permissions from backend', {
		userId: state.user.id,
		permissionCount: permissionsData.direct_permissions.length
	});

	// Update the auth state with fresh permissions
	const permissions: UserPermissions = {
		dashboards: {},
		features: {},
		direct_permissions: permissionsData.direct_permissions,
		inherited_permissions: [],
		metadata: {}
	};

	state.permissions = permissions;

	// PHASE 3 SECURITY: Persist to sessionStorage
	if (browser) {
		try {
			secureStorage.setPermissions(permissions);
			logger.debug('Permissions persisted to sessionStorage');
		} catch (e) {
			logger.error('Failed to save permissions to sessionStorage', {
				error: e instanceof Error ? e.message : 'Unknown error'
			});
		}

		// Emit event to notify components
		eventBus.emit('permissions:refreshed', { permissions });
		logger.debug('Permission refresh event emitted');
	}

	// Log specific ALERT permissions for debugging
	const alertPerms = permissions.direct_permissions.filter(
		(p) =>
			p.resource === 'ALERT_REVERSION' || p.resource === 'ALERT' || p.resource === 'APPROVED_ALERT'
	);
	if (alertPerms.length > 0) {
		logger.debug('Alert permissions after refresh', {
			alertPermissionCount: alertPerms.length,
			resources: alertPerms.map((p) => p.resource)
		});
	}
}

/**
 * Set token refreshing state
 */
function setTokenRefreshingInternal(isRefreshing: boolean) {
	if (tokenStateInternal.isRefreshing !== isRefreshing) {
		logger.debug('Token refresh state changed', { isRefreshing });
		tokenStateInternal.isRefreshing = isRefreshing;

		// PHASE 3.3: Update state machine status during token refresh
		if (isRefreshing) {
			state.status = 'refreshing';
		} else {
			// Only set to authenticated if we have valid token
			if (state.authenticated && state.tokenExpiry && state.tokenExpiry.getTime() > Date.now()) {
				state.status = 'authenticated';
			}
			tokenStateInternal.lastRefreshTime = new Date();
		}
	}
}

/**
 * Start auth check
 */
function startAuthCheckInternal() {
	logger.debug('Starting authentication check');
	if (!state.checking) {
		// PHASE 3.3: Update state machine status
		state.status = 'authenticating';
		state.checking = true;
		state.ready = false;
		state.error = null;
	}
}

/**
 * Set auth error
 */
function setAuthErrorInternal(error: Error) {
	logger.error('Authentication error occurred', { error: error.message });

	// PHASE 3.3: Update state machine status
	state.status = 'error';
	state.authenticated = false;
	state.user = null;
	state.permissions = null;
	state.tokenExpiry = null;
	state.error = error;
	state.checking = false;
	state.ready = true;
}

/**
 * Complete auth check
 */
function completeAuthCheckInternal() {
	if (state.checking) {
		state.checking = false;
		state.ready = true;
		logger.debug('Authentication check completed', { authenticated: state.authenticated });
	}
}

/**
 * Update token expiry while maintaining authenticated state during refresh
 */
function setTokenExpiryInternal(newExpiry: Date | null) {
	if (state.tokenExpiry?.getTime() !== newExpiry?.getTime()) {
		// Store old expiry to check if this is an extension (refresh) vs expiry
		const oldExpiry = state.tokenExpiry;
		const wasAuthenticated = state.authenticated;
		
		state.tokenExpiry = newExpiry;
		logger.debug('Token expiry updated', {
			oldExpiry: oldExpiry?.toISOString() ?? 'null',
			newExpiry: newExpiry?.toISOString() ?? 'null',
			wasAuthenticated
		});

		// PHASE 3 SECURITY: Use sessionStorage for token expiry
		if (browser) {
			if (newExpiry) {
				secureStorage.setTokenExpiry(newExpiry);

				// PHASE 3.2: Broadcast token refresh to other tabs
				if (authChannel) {
					authChannel.postMessage({
						type: 'token-refresh',
						expiry: newExpiry.toISOString()
					});
					logger.debug('[Auth] Token refresh broadcast to other tabs');
				}
				
				// If we had an old expiry and the new one is later, we're refreshing (not expiring)
				// So maintain authenticated state
				if (oldExpiry && newExpiry.getTime() > oldExpiry.getTime()) {
					logger.debug('Token refresh detected, maintaining authenticated state');
					// Only update status if we were already authenticated
					if (wasAuthenticated) {
						state.authenticated = true;
						// Don't change status to 'expired' if this is a refresh
						if (state.status === 'expired' && !tokenStateInternal.isRefreshing) {
							state.status = 'authenticated';
						}
					}
				}
			} else {
				secureStorage.clearTokenExpiry();
			}
		}
	}
}

/**
 * Update token timestamp
 */
function updateTokenTimestampInternal() {
	if (browser && state.authenticated && state.tokenExpiry) {
		// PHASE 3 SECURITY: Use sessionStorage for token timestamp
		secureStorage.updateTokenTimestamp();
		logger.debug('Token timestamp updated');
	}
}

/**
 * Navigate to login page with optional redirect
 */
function redirectToLoginInternal(returnUrl?: string): void {
	if (!browser) return;

	const url = returnUrl ? `/login?redirect=${encodeURIComponent(returnUrl)}` : '/login';

	goto(url);
}

// --- Effects ---

// Verify initial session (actions will be imported and called by AuthService)
$effect.root(() => {
	if (browser && state.checking && state.authenticated && !state.error) {
		logger.debug('Initial session requires verification by AuthService');
		// AuthService will handle verification and call completeAuthCheck
	}
});

// PHASE 3.2: Cross-tab synchronization with BroadcastChannel
// BroadcastChannel enables communication between tabs (sessionStorage doesn't fire events)
$effect.root(() => {
	if (!browser || !authChannel) return;

	const handleAuthMessage = (event: MessageEvent) => {
		logger.debug('[Auth] Received cross-tab message:', event.data);

		// Handle logout in another tab
		if (event.data.type === 'logout') {
			logger.info('[Auth] Logout detected in another tab');
			clearAuthenticationInternal({ skipBroadcast: true });
			goto('/login');
		}

		// Handle token refresh in another tab
		if (event.data.type === 'token-refresh' && event.data.expiry) {
			logger.info('[Auth] Token refresh detected in another tab');
			try {
				state.tokenExpiry = new Date(event.data.expiry);
				logger.debug('[Auth] Token expiry synchronized across tabs', {
					newExpiry: state.tokenExpiry.toISOString()
				});
			} catch (e) {
				logger.error('[Auth] Failed to parse token expiry from cross-tab message', {
					error: e instanceof Error ? e.message : 'Unknown error'
				});
			}
		}

		// Handle login in another tab
		// NOTE: The sender only broadcasts { type: 'login' } without user data,
		// so we restore from secureStorage (which is shared across tabs via sessionStorage)
		if (event.data.type === 'login') {
			logger.info('[Auth] Login detected in another tab');
			// Restore session from secureStorage
			const userData = secureStorage.getUserData();
			const permissions = secureStorage.getPermissions();
			const tokenExpiry = secureStorage.getTokenExpiry();

			if (userData) {
				state.user = userData;
				state.permissions = permissions;
				state.authenticated = true;
				state.status = 'authenticated';
				state.tokenExpiry = tokenExpiry;
				state.ready = true;
				state.checking = false;
				logger.debug('[Auth] Session synchronized from another tab', {
					userId: userData.id
				});
			}
		}
	};

	authChannel.addEventListener('message', handleAuthMessage);

	return () => {
		authChannel.removeEventListener('message', handleAuthMessage);
	};
});

// PHASE 3.4: Reactive token refresh timer
// Automatically refreshes token 15 minutes before expiry with 3-minute minimum buffer
// Handles sleep/wake, token expiry changes, and logout
$effect.root(() => {
	if (!browser || state.status !== 'authenticated' || !state.tokenExpiry) {
		return;
	}

	const msUntilExpiry = state.tokenExpiry.getTime() - Date.now();
	
	// Refresh 15 minutes before expiry with a 3-minute minimum buffer to prevent race conditions
	const refreshBuffer = Math.max(15 * 60 * 1000, msUntilExpiry * 0.15); // 15% of remaining time
	const refreshAt = Math.max(3 * 60 * 1000, msUntilExpiry - refreshBuffer); // At least 3 min before expiry

	if (refreshAt <= 0) {
		// Token already expired or expiring soon, refresh immediately
		logger.warn('[Auth] Token expiring soon, refreshing immediately');
		// Note: tokenService.refreshToken() should be called from auth.actions.svelte.ts
		// This is just logging the event - actual refresh happens elsewhere
		window.dispatchEvent(new CustomEvent('auth:token-needs-refresh'));
		return;
	}

	logger.debug(`[Auth] Scheduling token refresh in ${Math.floor(refreshAt / 1000)}s`);
	const timer = setTimeout(() => {
		logger.info('[Auth] Auto-refreshing token (scheduled)');
		// Dispatch event for token refresh - actual refresh handled by AuthService
		window.dispatchEvent(new CustomEvent('auth:token-auto-refresh'));
	}, refreshAt);

	// Cleanup function (prevents memory leaks)
	return () => {
		logger.debug('[Auth] Clearing token refresh timer');
		clearTimeout(timer);
	};
});

// --- Exported API ---

// Export state for direct access (read-only, wrapped in getters)
export const authState = {
	// Read-only state accessors
	get authenticated() {
		return state.authenticated;
	},
	get user() {
		return state.user;
	},
	get permissions() {
		return state.permissions;
	},
	get tokenExpiry() {
		return state.tokenExpiry;
	},
	get checking() {
		return state.checking;
	},
	get error() {
		return state.error;
	},
	get ready() {
		return state.ready;
	},

	// Actions (public API)
	setAuthenticatedFromLogin: setAuthenticatedFromLoginInternal,
	setAuthenticated: setAuthenticatedInternal,
	updateUserProfile: updateUserProfileInternal,
	clearAuthentication: clearAuthenticationInternal,
	setFreshPermissions: setFreshPermissionsInternal,
	startAuthCheck: startAuthCheckInternal,
	setAuthError: setAuthErrorInternal,
	completeAuthCheck: completeAuthCheckInternal,
	setTokenExpiry: setTokenExpiryInternal,
	updateTokenTimestamp: updateTokenTimestampInternal,
	redirectToLogin: redirectToLoginInternal
};

export const tokenState = {
	get isRefreshing() {
		return tokenStateInternal.isRefreshing;
	},
	get lastRefreshTime() {
		return tokenStateInternal.lastRefreshTime;
	},
	setTokenRefreshing: setTokenRefreshingInternal
};

// --- Legacy Function Exports for Backward Compatibility ---
// These match the existing API to minimize breaking changes
export function isAuthenticated(): boolean {
	return _isAuthenticated;
}

export function isAuthReady(): boolean {
	return _isAuthReady;
}

export function isCheckingAuth(): boolean {
	return _isCheckingAuth;
}

export function isAdmin(): boolean {
	return _isAdmin;
}

export function getUserName(): string {
	return _getUserName;
}

export function getUserEmail(): string {
	return _getUserEmail;
}

export function getTokenRemainingTime(): number {
	if (!state.tokenExpiry) return 0;
	const remaining = state.tokenExpiry.getTime() - Date.now();
	return Math.max(0, remaining);
}

export function shouldRefreshToken(): boolean {
	if (!state.tokenExpiry) return false;
	const remaining = getTokenRemainingTime();
	// Refresh if less than 5 minutes remaining
	return remaining < 300000;
}

// --- Additional Helper Function Exports ---
export function getUserId(): string | undefined {
	return _getUserId;
}

export function getUserLevel() {
	return _getUserLevel;
}

export function isCompanyUser(): boolean {
	return _isCompanyUser;
}

export function getAccessToken(): string | null {
	return _getAccessToken;
}

export function getTokenType(): string {
	return _getTokenType;
}

export function getAuthHeader(): string | null {
	return _getAuthHeader;
}

// --- Organization and Permission Function Exports ---
// These were previously exported as derived constants but Svelte 5 doesn't allow that
export function getOrganizationCode(): string | undefined {
	return _getOrganizationCode;
}

export function getOrganizationName(): string | undefined {
	return _getOrganizationName;
}

export function getOrganizationId(): string | undefined {
	return _getOrganizationId;
}

export function hasRole(): (roleCode: string) => boolean {
	return _hasRole;
}

export function hasDepartment(): (deptCode: string) => boolean {
	return _hasDepartment;
}

export function isDepartmentManager(): (deptCode?: string) => boolean {
	return _isDepartmentManager;
}

export function hasPermission(): (
	resource: ResourceType | string,
	action: ActionType | string
) => boolean {
	return _hasPermission;
}

// --- Dashboard and Component Permission Function Exports ---
// Consolidated from permissionStore.ts for unified permission management

export function canAccessDashboard(): (dashboardKey: string) => boolean {
	return _canAccessDashboard;
}

export function canAccessComponent(): (dashboardKey: string, componentKey: string) => boolean {
	return _canAccessComponent();
}

export function canPerformAction(): (
	dashboardKey: string,
	componentKey: string,
	action: string
) => boolean {
	return _canPerformAction();
}

export function hasFeature(): (featureKey: string) => boolean {
	return _hasFeature;
}

// --- Backward Compatibility Action Exports ---
// Export actions as named functions for existing consumers
export const setAuthenticatedFromLogin = setAuthenticatedFromLoginInternal;
export const setAuthenticated = setAuthenticatedInternal;
export const updateUserProfile = updateUserProfileInternal;
export const clearAuthentication = clearAuthenticationInternal;
export const setFreshPermissions = setFreshPermissionsInternal;
export const setTokenRefreshing = setTokenRefreshingInternal;
export const startAuthCheck = startAuthCheckInternal;
export const setAuthError = setAuthErrorInternal;
export const completeAuthCheck = completeAuthCheckInternal;
export const setTokenExpiry = setTokenExpiryInternal;
export const updateTokenTimestamp = updateTokenTimestampInternal;
export const redirectToLogin = redirectToLoginInternal;
